<script setup lang="ts">
import api from "@/api";
import { APP_NAME, getPageTitle } from "@/assets/js/appConfig";
import { setLocalStorageReac } from "@/assets/js/helpers";
import { useDefaultStore } from "@/store";
import { useToast } from "primevue/usetoast";

definePage({
	name: "MoviesSettingsPage",
});

type TOption<T = string | number> = {
	label: string;
	value: T;
};

const store = useDefaultStore();
const toast = useToast();
const router = useRouter();

const username = ref("");
const initialUsername = ref("");

const selectedGenreIds = ref<number[]>([]);
const initialGenreIds = ref<number[]>([]);

const selectedOriginCountryCode = ref<string | null>(null);
const initialOriginCountryCode = ref<string | null>(null);

const minYear = ref("");
const initialMinYear = ref("");

const maxYear = ref("");
const initialMaxYear = ref("");

const showAll = ref(false);
const initialShowAll = ref(false);

const showOriginalTitle = ref(false);
const initialShowOriginalTitle = ref(false);

const genreOptions = ref<TOption<number>[]>([]);
const originCountryOptions = ref<TOption<string>[]>([]);

const isLoading = ref(true);
const isSaving = ref(false);

function normalizeGenreIds(raw: unknown): number[] {
	if (!Array.isArray(raw)) {
		return [];
	}

	return [
		...new Set(
			raw
				.map((item: any) => {
					if (typeof item === "number") {
						return item;
					}

					if (typeof item === "string") {
						return Number(item);
					}

					if (item && typeof item === "object") {
						return Number(
							item.id ??
								item.genreId ??
								item.mog_id ??
								item.value ??
								item.mov_gen_id ??
								item.gen_id,
						);
					}

					return NaN;
				})
				.filter((value) => Number.isFinite(value)),
		),
	].sort((a, b) => a - b);
}

function normalizeOriginCountryCode(raw: unknown): string | null {
	if (!raw) {
		return null;
	}

	if (typeof raw === "string") {
		return raw || null;
	}

	if (raw && typeof raw === "object") {
		return (
			String(
				(raw as any).code ?? (raw as any).moc_code ?? (raw as any).value ?? "",
			) || null
		);
	}

	return null;
}

function normalizeYear(raw: unknown): string {
	if (raw === null || raw === undefined || raw === "") {
		return "";
	}

	const value = Number(raw);

	if (!Number.isFinite(value)) {
		return "";
	}

	return String(Math.trunc(value));
}

function normalizeShowAll(raw: unknown): boolean {
	if (typeof raw === "boolean") {
		return raw;
	}

	if (typeof raw === "string") {
		return raw === "all" || raw === "true" || raw === "1";
	}

	if (typeof raw === "number") {
		return raw === 1;
	}

	return false;
}

function arraysEqual(a: number[], b: number[]) {
	if (a.length !== b.length) {
		return false;
	}

	return a.every((value, index) => value === b[index]);
}

function extractFirstArray(...values: any[]): any[] {
	for (const value of values) {
		if (Array.isArray(value)) {
			return value;
		}
	}

	return [];
}

function mapGenreOptions(items: any[]): TOption<number>[] {
	return items
		.map((item) => ({
			label: String(
				item?.name ?? item?.genre_name ?? item?.mog_name ?? item?.label ?? "",
			).trim(),
			value: Number(
				item?.id ??
					item?.genreId ??
					item?.mog_id ??
					item?.value ??
					item?.mov_gen_id ??
					item?.gen_id,
			),
		}))
		.filter((item) => item.label && Number.isFinite(item.value))
		.filter(
			(item, index, array) =>
				array.findIndex((x) => x.value === item.value) === index,
		)
		.sort((a, b) => a.label.localeCompare(b.label));
}

function mapOriginCountryOptions(items: any[]): TOption<string>[] {
	return items
		.map((item) => ({
			label: String(item?.english_name ?? "").trim(),
			value: String(item?.iso_3166_1 ?? "").trim(),
		}))
		.filter((item) => item.label && item.value)
		.filter(
			(item, index, array) =>
				array.findIndex((x) => x.value === item.value) === index,
		)
		.sort((a, b) => a.label.localeCompare(b.label));
}

function syncLocalStateFromStore() {
	const currentUsername = store.userData?.usr_username ?? "";

	username.value = currentUsername;
	initialUsername.value = currentUsername;

	const userGenreIds = normalizeGenreIds(store.userData?.usr_mov_gnr_ids);

	selectedGenreIds.value = [...userGenreIds];
	initialGenreIds.value = [...userGenreIds];

	const userOriginCountryCode = normalizeOriginCountryCode(
		store.userData?.usr_mov_ori_cty_code,
	);

	selectedOriginCountryCode.value = userOriginCountryCode;
	initialOriginCountryCode.value = userOriginCountryCode;

	const userMinYear = normalizeYear(store.userData?.usr_movies_min_year);
	const userMaxYear = normalizeYear(store.userData?.usr_movies_max_year);

	minYear.value = userMinYear;
	initialMinYear.value = userMinYear;

	maxYear.value = userMaxYear;
	initialMaxYear.value = userMaxYear;

	const userShowAll = normalizeShowAll(store.userData?.usr_movies_popularity);

	showAll.value = userShowAll;
	initialShowAll.value = userShowAll;

	const userShowOriginalTitle = normalizeShowAll(
		store.userData?.usr_movies_show_original_title,
	);

	showOriginalTitle.value = userShowOriginalTitle;
	initialShowOriginalTitle.value = userShowOriginalTitle;
}

const hasChanges = computed(() => {
	const normalizedSelectedGenreIds = [...selectedGenreIds.value].sort(
		(a, b) => a - b,
	);

	const normalizedMinYear = normalizeYearInputValue(minYear.value);
	const normalizedInitialMinYear = normalizeYearInputValue(
		initialMinYear.value,
	);

	const normalizedMaxYear = normalizeYearInputValue(maxYear.value);
	const normalizedInitialMaxYear = normalizeYearInputValue(
		initialMaxYear.value,
	);

	return (
		username.value.trim() !== initialUsername.value.trim() ||
		!arraysEqual(normalizedSelectedGenreIds, initialGenreIds.value) ||
		selectedOriginCountryCode.value !== initialOriginCountryCode.value ||
		normalizedMinYear !== normalizedInitialMinYear ||
		normalizedMaxYear !== normalizedInitialMaxYear ||
		showAll.value !== initialShowAll.value ||
		showOriginalTitle.value !== initialShowOriginalTitle.value
	);
});

async function loadSettings() {
	try {
		isLoading.value = true;

		if (!store.userData?.usr_token) {
			await store.getUser();
		}

		const [genresRes, countriesRes] = await Promise.all([
			api.getMovieGenres?.({ token: store.userData?.usr_token ?? "" }) ??
				Promise.resolve(null),
			api.getMovieOriginCountries?.({
				token: store.userData?.usr_token ?? "",
			}) ?? Promise.resolve(null),
		]);

		const genresPayload = extractFirstArray(genresRes?.data?.data?.genres);
		const countriesPayload = extractFirstArray(
			countriesRes?.data?.data?.countries,
		);

		genreOptions.value = mapGenreOptions(genresPayload);
		originCountryOptions.value = mapOriginCountryOptions(countriesPayload);

		syncLocalStateFromStore();
	} catch (err: any) {
		console.warn(err.message);
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "Unable to load user settings.",
			life: 3000,
		});
	} finally {
		isLoading.value = false;
	}
}

async function saveSettings() {
	const trimmedName = username.value.trim();
	const token = store.userData?.usr_token ?? "";
	const currentYear = new Date().getFullYear();

	if (!token) {
		toast.add({
			severity: "error",
			summary: "Error",
			detail: "Missing user token.",
			life: 3000,
		});
		return;
	}

	// if (!trimmedName) {
	// 	toast.add({
	// 		severity: "warn",
	// 		summary: "Validation",
	// 		detail: "Username cannot be empty.",
	// 		life: 2500,
	// 	});
	// 	return;
	// }

	const normalizedMinYear = parseOptionalYear(minYear.value);
	const normalizedMaxYear = parseOptionalYear(maxYear.value);

	if (
		normalizedMinYear !== null &&
		(!Number.isInteger(normalizedMinYear) ||
			normalizedMinYear < 1888 ||
			normalizedMinYear > currentYear)
	) {
		toast.add({
			severity: "warn",
			summary: "Validation",
			detail: `Min year must be between 1888 and ${currentYear}.`,
			life: 2500,
		});
		return;
	}

	if (
		normalizedMaxYear !== null &&
		(!Number.isInteger(normalizedMaxYear) ||
			normalizedMaxYear < 1888 ||
			normalizedMaxYear > currentYear)
	) {
		toast.add({
			severity: "warn",
			summary: "Validation",
			detail: `Max year must be between 1888 and ${currentYear}.`,
			life: 2500,
		});
		return;
	}

	if (
		normalizedMinYear !== null &&
		normalizedMaxYear !== null &&
		normalizedMinYear > normalizedMaxYear
	) {
		toast.add({
			severity: "warn",
			summary: "Validation",
			detail: "Min year cannot be greater than max year.",
			life: 2500,
		});
		return;
	}

	if (!hasChanges.value) {
		return;
	}

	try {
		isSaving.value = true;

		const normalizedGenreIds = [...selectedGenreIds.value].sort(
			(a, b) => a - b,
		);

		const res = await api.editUser({
			token,
			name: trimmedName,
			genreIds: normalizedGenreIds,
			originCountryCodes: selectedOriginCountryCode.value,
			minMovieYear: normalizedMinYear,
			maxMovieYear: normalizedMaxYear,
			moviePopularity: showAll.value ? "all" : "popular",
			showOriginalTitle: showOriginalTitle.value,
		} as any);

		const data = res.data.data;

		if (!data?.ok) {
			toast.add({
				severity: "error",
				summary: "Error",
				detail: data?.showText
					? (data?.message ?? "Unable to save settings.")
					: "Unable to save settings.",
				life: 3500,
			});

			await router.push({
				name: "MoviesPage",
			});

			return;
		}

		await store.getUser();

		const updatedUsername = store.userData?.usr_username ?? trimmedName;

		username.value = updatedUsername;
		initialUsername.value = updatedUsername;
		setLocalStorageReac("username", username.value);

		initialGenreIds.value = [...normalizedGenreIds];
		initialOriginCountryCode.value = selectedOriginCountryCode.value;
		initialMinYear.value =
			normalizedMinYear === null ? "" : String(normalizedMinYear);
		initialMaxYear.value =
			normalizedMaxYear === null ? "" : String(normalizedMaxYear);
		initialShowAll.value = showAll.value;

		minYear.value = initialMinYear.value;
		maxYear.value = initialMaxYear.value;

		initialShowOriginalTitle.value = showOriginalTitle.value;

		toast.add({
			severity: "success",
			summary: "Saved",
			detail: "Settings updated successfully.",
			life: 2500,
		});
	} catch (err: any) {
		console.warn(err.message);

		const showText = err?.response?.data?.data?.showText;
		const customErrorMsg = err?.response?.data?.data?.message;

		toast.add({
			severity: "error",
			summary: "Error",
			detail: showText
				? (customErrorMsg ?? "Unable to save settings.")
				: "Unable to save settings.",
			life: 3500,
		});

		await router.push({
			name: "MoviesPage",
		});
	} finally {
		isSaving.value = false;
	}
}

function normalizeYearInputValue(raw: unknown): string {
	if (raw === null || raw === undefined) {
		return "";
	}

	if (typeof raw === "number") {
		return Number.isFinite(raw) ? String(Math.trunc(raw)) : "";
	}

	if (typeof raw === "string") {
		return raw.trim();
	}

	return String(raw).trim();
}

function parseOptionalYear(raw: unknown): number | null {
	const normalized = normalizeYearInputValue(raw);

	if (!normalized) {
		return null;
	}

	const value = Number(normalized);

	if (!Number.isFinite(value)) {
		return null;
	}

	return Math.trunc(value);
}

onMounted(() => {
	loadSettings();
});

useHead({
	title: getPageTitle("Settings"),
	meta: [
		{
			name: "title",
			content: getPageTitle("Settings"),
		},
		{
			name: "description",
			content: `Manage your ${APP_NAME} account settings.`,
		},
		{ name: "robots", content: "noindex, nofollow" },
		{
			property: "og:title",
			content: getPageTitle("Settings"),
		},
		{
			property: "og:description",
			content: `Manage your ${APP_NAME} account settings.`,
		},
		{ property: "og:site_name", content: APP_NAME },
		{
			name: "viewport",
			content: "width=device-width, initial-scale=1, viewport-fit=cover",
		},
	],
});
</script>

<template>
	<section class="settings-page">
		<div class="overlay"></div>

		<div class="page-shell">
			<div class="settings-card">
				<RouterLink
					to="/movies"
					class="category-back-link"
				>
					← Back
				</RouterLink>

				<div class="header">
					<p class="eyebrow">Settings</p>
					<h1 class="title">Profile Settings</h1>
				</div>

				<div
					v-if="isLoading"
					class="state-box"
				>
					Loading settings...
				</div>

				<div
					v-else
					class="form-block"
				>
					<div class="field-group">
						<label
							for="username"
							class="field-label"
						>
							Username
						</label>

						<input
							id="username"
							v-model="username"
							type="text"
							class="field-input"
							maxlength="40"
							placeholder="Enter username"
							:disabled="isSaving"
						/>
					</div>

					<div class="field-group">
						<label class="field-label">Titles</label>

						<label class="checkbox-row">
							<input
								v-model="showOriginalTitle"
								type="checkbox"
								class="checkbox-input"
								:disabled="isSaving"
							/>

							<span class="checkbox-box"></span>
							<span class="checkbox-text">Show original titles</span>
						</label>

						<p class="checkbox-note">
							When enabled, movies will be displayed using their original
							titles.
						</p>
					</div>

					<p class="settings-note">
						Customize your movie pool by choosing preferred genres, an origin
						country, and a release year range.
					</p>

					<div class="field-group">
						<label class="field-label">Genres</label>

						<MultiSelect
							v-model="selectedGenreIds"
							:options="genreOptions"
							optionLabel="label"
							optionValue="value"
							filter
							display="chip"
							:maxSelectedLabels="3"
							placeholder="Select genres"
							class="field-control"
							:pt="{
								overlay: { class: 'settings-overlay-panel' },
							}"
							:disabled="isSaving"
						/>
					</div>

					<div class="field-group">
						<label class="field-label">Origin Country</label>

						<Select
							v-model="selectedOriginCountryCode"
							:options="originCountryOptions"
							optionLabel="label"
							optionValue="value"
							filter
							showClear
							placeholder="Select origin country"
							class="field-control"
							:pt="{
								overlay: { class: 'settings-overlay-panel' },
							}"
							:disabled="isSaving"
						/>
					</div>

					<div class="year-grid">
						<div class="field-group">
							<label
								for="min-year"
								class="field-label"
							>
								Min Year
							</label>

							<input
								id="min-year"
								v-model="minYear"
								type="number"
								inputmode="numeric"
								class="field-input"
								min="1888"
								:max="new Date().getFullYear()"
								placeholder="e.g. 1980"
								:disabled="isSaving"
							/>
						</div>

						<div class="field-group">
							<label
								for="max-year"
								class="field-label"
							>
								Max Year
							</label>

							<input
								id="max-year"
								v-model="maxYear"
								type="number"
								inputmode="numeric"
								class="field-input"
								min="1888"
								:max="new Date().getFullYear()"
								placeholder="e.g. 2024"
								:disabled="isSaving"
							/>
						</div>
					</div>

					<div class="field-group">
						<label class="field-label">Popularity</label>

						<label class="checkbox-row">
							<input
								v-model="showAll"
								type="checkbox"
								class="checkbox-input"
								:disabled="isSaving"
							/>

							<span class="checkbox-box"></span>
							<span class="checkbox-text">Show all</span>
						</label>

						<p class="checkbox-note">
							By checking this, the popularity filter will be ignored to
							provide more results for your selected filters (not recommended).
						</p>
					</div>

					<button
						type="button"
						class="save-button"
						:disabled="isSaving || !hasChanges"
						@click="saveSettings"
					>
						{{ isSaving ? "Saving..." : "Save Changes" }}
					</button>
				</div>
			</div>
		</div>
	</section>
</template>

<style scoped lang="scss">
.field-group {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.settings-note {
	margin: -2px 0 2px;
	font-size: 14px;
	line-height: 1.55;
	color: rgba(255, 255, 255, 0.68);
}

.field-input[type="number"] {
	appearance: textfield;
}

.field-input[type="number"]::-webkit-outer-spin-button,
.field-input[type="number"]::-webkit-inner-spin-button {
	appearance: none;
	margin: 0;
}

.year-grid {
	display: grid;
	grid-template-columns: 1fr;
	gap: 18px;
}

.checkbox-row {
	display: inline-flex;
	align-items: center;
	gap: 10px;
	cursor: pointer;
	user-select: none;
	width: fit-content;
}

.checkbox-input {
	position: absolute;
	opacity: 0;
	pointer-events: none;
}

.checkbox-box {
	width: 20px;
	height: 20px;
	border-radius: 6px;
	border: 1px solid rgba(255, 255, 255, 0.18);
	background: rgba(255, 255, 255, 0.06);
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition:
		background 0.2s ease,
		border-color 0.2s ease,
		box-shadow 0.2s ease;
}

.checkbox-box::after {
	content: "";
	width: 10px;
	height: 10px;
	border-radius: 3px;
	background: rgba(255, 255, 255, 0.9);
	transform: scale(0);
	transition: transform 0.18s ease;
}

.checkbox-input:checked + .checkbox-box {
	background: rgba(255, 255, 255, 0.16);
	border-color: rgba(255, 255, 255, 0.24);
}

.checkbox-input:checked + .checkbox-box::after {
	transform: scale(1);
}

.checkbox-input:focus + .checkbox-box {
	box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.06);
}

.checkbox-text {
	font-size: 15px;
	font-weight: 600;
	color: $text-color-white;
}

.checkbox-note {
	margin: 0;
	font-size: 13px;
	line-height: 1.5;
	color: rgba(255, 255, 255, 0.62);
}

.field-control {
	width: 100%;
}

:deep(.field-control.p-select),
:deep(.field-control.p-multiselect) {
	width: 100%;
	border-radius: 16px;
	border: 1px solid rgba(255, 255, 255, 0.14) !important;
	background: rgba(255, 255, 255, 0.08) !important;
	color: $text-color-white !important;
	backdrop-filter: blur(8px);
	box-shadow: none !important;
	transition:
		border-color 0.2s ease,
		background 0.2s ease,
		box-shadow 0.2s ease;
}

:deep(.field-control.p-select) {
	height: 50px;
	min-height: 50px;
}

:deep(.field-control.p-multiselect) {
	min-height: 50px;
}

:deep(.field-control.p-select:not(.p-disabled):hover),
:deep(.field-control.p-multiselect:not(.p-disabled):hover) {
	border-color: rgba(255, 255, 255, 0.2) !important;
	background: rgba(255, 255, 255, 0.1) !important;
}

:deep(.field-control.p-select.p-focus),
:deep(.field-control.p-multiselect.p-focus) {
	border-color: rgba(255, 255, 255, 0.28) !important;
	background: rgba(255, 255, 255, 0.1) !important;
	box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.06) !important;
}

:deep(.field-control.p-select .p-select-label) {
	display: flex;
	align-items: center;
	height: 100%;
	padding: 0 16px !important;
	background: transparent !important;
	color: $text-color-white !important;
	font-size: 15px;
}

:deep(.field-control.p-multiselect .p-multiselect-label-container) {
	display: flex;
	align-items: center;
	min-height: 48px;
	background: transparent !important;
	padding: 0 0 0 12px !important;
}

:deep(.field-control.p-multiselect .p-multiselect-label) {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	align-content: center;
	gap: 6px;
	width: 100%;
	min-height: 48px;
	padding: 6px 0 !important;
	background: transparent !important;
	color: $text-color-white !important;
	font-size: 15px;
	line-height: 1.2;
}

:deep(.field-control.p-select .p-placeholder),
:deep(.field-control.p-multiselect .p-placeholder),
:deep(.field-control.p-select .p-select-label.p-placeholder),
:deep(.field-control.p-multiselect .p-multiselect-label.p-placeholder) {
	color: rgba(255, 255, 255, 0.45) !important;
}

:deep(.field-control.p-select .p-select-dropdown),
:deep(.field-control.p-multiselect .p-multiselect-dropdown),
:deep(.field-control.p-multiselect .p-multiselect-trigger) {
	width: 44px;
	min-width: 44px;
	background: transparent !important;
	border: 0 !important;
	box-shadow: none !important;
	color: rgba(255, 255, 255, 0.72) !important;
}

:deep(.field-control.p-select .p-select-clear-icon),
:deep(.field-control.p-multiselect .p-multiselect-clear-icon) {
	color: rgba(255, 255, 255, 0.62) !important;
}

:deep(.field-control.p-multiselect .p-chip) {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	height: 28px;
	padding: 0 10px;
	border-radius: 999px;
	background: rgba(255, 255, 255, 0.12) !important;
	border: 1px solid rgba(255, 255, 255, 0.08);
	color: $text-color-white !important;
	font-size: 13px;
	font-weight: 600;
	max-width: 100%;
	margin: 0;
}

:deep(.field-control.p-multiselect .p-chip-label) {
	color: $text-color-white !important;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

:deep(.field-control.p-multiselect .p-chip-remove-icon) {
	color: rgba(255, 255, 255, 0.72) !important;
	font-size: 12px;
}

:global(.settings-overlay-panel) {
	margin-top: 8px;
	border-radius: 18px;
	border: 1px solid rgba(255, 255, 255, 0.12);
	background: rgba(14, 18, 28, 0.94) !important;
	backdrop-filter: blur(14px);
	box-shadow: 0 18px 40px rgba(0, 0, 0, 0.28);
	overflow: hidden;
}

:global(.settings-overlay-panel .p-select-header),
:global(.settings-overlay-panel .p-multiselect-header) {
	padding: 10px 12px;
	background: rgba(255, 255, 255, 0.04) !important;
	border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

:global(.settings-overlay-panel .p-select-filter),
:global(.settings-overlay-panel .p-multiselect-filter) {
	width: 100%;
	height: 42px;
	padding: 0 12px;
	border-radius: 12px;
	border: 1px solid rgba(255, 255, 255, 0.1);
	background: rgba(255, 255, 255, 0.06) !important;
	color: $text-color-white !important;
	outline: none;
	box-shadow: none;
}

:global(.settings-overlay-panel .p-select-filter::placeholder),
:global(.settings-overlay-panel .p-multiselect-filter::placeholder) {
	color: rgba(255, 255, 255, 0.45);
}

:global(.settings-overlay-panel .p-select-list-container),
:global(.settings-overlay-panel .p-multiselect-list-container),
:global(.settings-overlay-panel .p-select-list),
:global(.settings-overlay-panel .p-multiselect-list) {
	background: transparent !important;
}

:global(.settings-overlay-panel .p-select-option),
:global(.settings-overlay-panel .p-multiselect-option) {
	color: rgba(255, 255, 255, 0.88) !important;
	background: transparent !important;
	transition:
		background 0.18s ease,
		color 0.18s ease;
}

:global(.settings-overlay-panel .p-select-option:hover),
:global(.settings-overlay-panel .p-multiselect-option:hover) {
	background: rgba(255, 255, 255, 0.08) !important;
	color: $text-color-white !important;
}

:global(.settings-overlay-panel .p-select-option.p-select-option-selected),
:global(
		.settings-overlay-panel .p-multiselect-option.p-multiselect-option-selected
	) {
	background: rgba(255, 255, 255, 0.12) !important;
	color: $text-color-white !important;
}

:global(.settings-overlay-panel .p-checkbox-box) {
	border-color: rgba(255, 255, 255, 0.18) !important;
	background: rgba(255, 255, 255, 0.04) !important;
}

:global(.settings-overlay-panel .p-checkbox-checked .p-checkbox-box) {
	background: rgba(255, 255, 255, 0.16) !important;
	border-color: rgba(255, 255, 255, 0.22) !important;
}

:global(.settings-overlay-panel .p-select-empty-message),
:global(.settings-overlay-panel .p-multiselect-empty-message) {
	padding: 14px 16px;
	color: rgba(255, 255, 255, 0.58) !important;
}

@media (min-width: 768px) {
	.settings-note {
		font-size: 15px;
	}

	.year-grid {
		grid-template-columns: 1fr 1fr;
	}
}
</style>
